<template>
  <v-container fluid>
    <v-row justify="center">
      <v-card width="95%" class="mt-5" elevation="0">
        <v-card-title style="background-color:#f4f4f5;">
          <v-icon large color="#1A3A5C" class="mr-3">mdi-account-check-outline</v-icon>
          <strong style="font-size:26px; color:#1A3A5C;">First Steps Users</strong>
          <v-spacer />
          <v-text-field
            v-model="search"
            dense outlined hide-details
            placeholder="Search name or email"
            append-icon="mdi-magnify"
            style="max-width:320px;"
          />
        </v-card-title>

        <v-card-text class="pa-0">
          <v-data-table
            :headers="headers"
            :items="filteredUsers"
            :loading="loading"
            :items-per-page="25"
            class="elevation-0"
            style="border-radius:16px;"
          >
            <template v-slot:loading>
              <v-progress-circular indeterminate color="#1A3A5C" class="my-5" />
            </template>

            <template #[`item.name`]="{ item }">
              <span class="font-weight-bold">{{ item.name }}</span><br />
              <span class="caption grey--text">{{ item.email }}</span>
            </template>

            <template #[`item.stripe_status`]="{ item }">
              <v-chip small :color="statusColor(item.stripe_status)" dark>
                {{ statusLabel(item.stripe_status) }}
              </v-chip>
            </template>

            <template #[`item.trial_ends_at`]="{ item }">
              <span v-if="item.stripe_status === 'trialing'">
                {{ item.trial_ends_at }}
                <span class="caption" :class="item.trial_days_left <= 1 ? 'red--text font-weight-bold' : 'grey--text'">
                  ({{ item.trial_days_left }}d left)
                </span>
              </span>
              <span v-else class="grey--text">—</span>
            </template>

            <template #[`item.subscription_ends_at`]="{ item }">
              {{ item.subscription_ends_at || '—' }}
            </template>

            <template #[`item.sections_filled`]="{ item }">
              <v-chip small :color="item.sections_filled > 0 ? '#1A3A5C' : 'grey lighten-2'" :dark="item.sections_filled > 0">
                {{ item.sections_filled }} / 12
              </v-chip>
            </template>

            <template #[`item.actions`]="{ item }">
              <div class="d-flex align-center" style="gap:6px;">
                <v-btn small depressed color="#1A3A5C" dark @click="$router.push({ name: '/admin_fs_user', params: { id: item.id } })">
                  View
                </v-btn>
                <v-btn
                  v-if="item.stripe_status === 'trialing'"
                  small depressed outlined color="#2E75B6"
                  @click="openEdit(item)"
                >
                  <v-icon small left>mdi-pencil</v-icon>Edit
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-row>

    <!-- Edit trial dialog -->
    <v-dialog v-model="editDialog" max-width="420" @click:outside="closeEdit">
      <v-card class="rounded-xl">
        <v-card-title class="text-h6 font-weight-bold" style="color:#1A3A5C;">
          Edit trial — {{ editUser && editUser.name }}
        </v-card-title>
        <v-card-text class="pt-2">
          <v-select
            v-model="editStatus"
            :items="statusOptions"
            label="Status"
            outlined dense
            class="mb-3"
          />
          <v-menu
            v-model="datePicker"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290"
          >
            <template #activator="{ on, attrs }">
              <v-text-field
                v-model="editTrialEndsAt"
                label="Trial ends"
                outlined dense
                readonly
                append-icon="mdi-calendar"
                v-bind="attrs"
                v-on="on"
              />
            </template>
            <v-date-picker
              v-model="editTrialEndsAt"
              :min="today"
              no-title
              @input="datePicker = false"
            />
          </v-menu>
          <v-alert v-if="editError" type="error" dense text class="mt-1">{{ editError }}</v-alert>
        </v-card-text>
        <v-card-actions class="px-6 pb-5">
          <v-spacer />
          <v-btn text @click="closeEdit">Cancel</v-btn>
          <v-btn depressed color="#1A3A5C" dark :loading="saving" @click="saveEdit">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
export default {
  name: 'FSUserList',
  data: () => ({
    users: [],
    loading: true,
    search: '',
    headers: [
      { text: 'User',              value: 'name',                 sortable: true  },
      { text: 'Signed up',         value: 'signed_up',            sortable: true  },
      { text: 'Verified',          value: 'email_verified_at',    sortable: true  },
      { text: 'Status',            value: 'stripe_status',        sortable: true  },
      { text: 'Trial ends',        value: 'trial_ends_at',        sortable: true  },
      { text: 'Subscription ends', value: 'subscription_ends_at', sortable: true  },
      { text: 'Sections filled',   value: 'sections_filled',      sortable: true  },
      { text: '',                  value: 'actions',              sortable: false },
    ],
    statusOptions: [
      { text: 'Trial',    value: 'trialing' },
      { text: 'Active',   value: 'active'   },
      { text: 'Canceled', value: 'canceled' },
    ],
    editDialog: false,
    editUser: null,
    editStatus: null,
    editTrialEndsAt: null,
    datePicker: false,
    saving: false,
    editError: null,
  }),

  computed: {
    filteredUsers () {
      if (!this.search) return this.users
      const q = this.search.toLowerCase()
      return this.users.filter(u =>
        (u.name || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q)
      )
    },
    today () {
      return new Date().toISOString().substring(0, 10)
    },
  },

  async mounted () {
    const res = await this.$axios.get('/auth/admin/fs_users/')
    if (res && res.data && res.data.response) {
      this.users = res.data.data
    }
    this.loading = false
  },

  methods: {
    statusColor (s) {
      const map = { trialing: '#2E75B6', active: 'success', lifetime: 'teal', past_due: 'orange', canceled: 'error', unpaid: 'error' }
      return map[s] || 'grey'
    },
    statusLabel (s) {
      const map = { trialing: 'Trial', active: 'Active', lifetime: 'Lifetime', past_due: 'Past due', canceled: 'Canceled', unpaid: 'Unpaid' }
      return map[s] || (s || 'Unknown')
    },

    openEdit (user) {
      this.editUser        = user
      this.editStatus      = user.stripe_status
      this.editTrialEndsAt = user.trial_ends_at || null
      this.editError       = null
      this.editDialog      = true
    },

    closeEdit () {
      this.editDialog      = false
      this.editUser        = null
      this.editStatus      = null
      this.editTrialEndsAt = null
      this.editError       = null
    },

    async saveEdit () {
      this.editError = null
      this.saving    = true
      try {
        const res = await this.$axios.patch(`/auth/admin/fs_users/${this.editUser.id}`, {
          stripe_status:  this.editStatus,
          trial_ends_at:  this.editTrialEndsAt,
        })
        if (res && res.data && res.data.response) {
          const idx = this.users.findIndex(u => u.id === this.editUser.id)
          if (idx !== -1) this.$set(this.users, idx, res.data.data)
          this.closeEdit()
        } else {
          this.editError = res?.data?.message || 'Save failed.'
        }
      } catch (e) {
        this.editError = e?.response?.data?.message || 'An error occurred.'
      }
      this.saving = false
    },
  },
}
</script>
